// src/utils/quoteUtils.js
// Pricing engine + PDF rendering for the Customer Quote Estimator.
// Pricing logic is a direct port of ACA_Quote_Estimator_Spec.md §2.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../services/supabase';
import { savePdfWithDialog } from './saveFile';

// ─────────────────────────────────────────────────────────────────────────────
// Rounding
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Round n half-away-from-zero (Excel's convention).
 * JS's Math.round rounds toward +Infinity for ties on negatives, which differs.
 *   Math.round(-0.5) === 0     (toward +Inf)
 *   roundHAFZ(-0.5) === -1     (away from zero)
 */
const roundHAFZ = (n) => {
  if (n === 0) return 0;
  return Math.sign(n) * Math.round(Math.abs(n));
};

/**
 * mround(value, multiple) — round value to the nearest integer multiple of `multiple`.
 * If multiple is 0 / null / undefined, returns value unchanged.
 *   mround(101.234, 0.01) === 101.23
 *   mround(101.236, 0.01) === 101.24
 *   mround(101.234, 1.00) === 101
 *   mround(101.234, 5.00) === 100
 *   mround(103.000, 5.00) === 105
 */
export const mround = (value, multiple) => {
  if (!multiple || multiple <= 0) return value;
  return roundHAFZ(value / multiple) * multiple;
};

// ─────────────────────────────────────────────────────────────────────────────
// Pricing
// ─────────────────────────────────────────────────────────────────────────────

const num = (v) => Number(v || 0);

/**
 * Price a single quantity break. Returns null for q <= 0 (unused slot).
 * `quote` is the customer_quotes row (or any object with the same fields).
 *
 * All labor fields are in MINUTES. Shop rate is per-hour, so labor cost = (minutes / 60) × rate.
 *
 * Amortized per-order:
 *   setup_min, programming_min, inspection_min, packaging_min,
 *   plating_total, tooling_total, fixturing_total, shipping_total, minimum_lot_charge
 *
 * Per-piece (NOT amortized):
 *   deburring_min, runtime_min, secondary_min, material_cost_per_part
 */
export const priceQuantityBreak = (quote, q) => {
  if (!q || q <= 0) return null;

  const rate = num(quote.shop_rate_per_hr);

  // Setup & Programming (per-order minutes, amortized across q)
  const setup_pp = (num(quote.setup_min)       / 60 * rate) / q;
  const prog_pp  = (num(quote.programming_min) / 60 * rate) / q;
  const total_setup_pp = setup_pp + prog_pp;

  // Fabrication (per-piece minutes — every part incurs these)
  const deburr_pp    = (num(quote.deburring_min) / 60) * rate;
  const runtime_pp   = (num(quote.runtime_min)   / 60) * rate;
  const secondary_pp = (num(quote.secondary_min) / 60) * rate;
  const total_fab_pp = deburr_pp + runtime_pp + secondary_pp;

  // Cost of Goods — mixed: material flat, others amortized
  const material_pp   = num(quote.material_cost_per_part);                       // flat
  const plating_pp    = num(quote.plating_total)        / q;                     // amortized
  const tooling_pp    = num(quote.tooling_total)        / q;                     // amortized
  const fixturing_pp  = num(quote.fixturing_total)      / q;                     // amortized
  const inspection_pp = (num(quote.inspection_min) / 60 * rate) / q;             // amortized labor (min)
  const packaging_pp  = (num(quote.packaging_min)  / 60 * rate) / q;             // amortized labor (min)
  const shipping_pp   = num(quote.shipping_total)       / q;                     // amortized
  const total_cog_pp  =
    material_pp + plating_pp + tooling_pp + fixturing_pp +
    inspection_pp + packaging_pp + shipping_pp;

  // Build the quote price
  const subtotal_pp = total_setup_pp + total_fab_pp + total_cog_pp;
  const markup_pp   = subtotal_pp * num(quote.markup_pct);
  const price_pp    = subtotal_pp + markup_pp;

  // Minimum lot floor (per-piece), applied BEFORE rounding
  const min_lot_pp  = num(quote.minimum_lot_charge) / q;
  const calc_pp     = Math.max(price_pp, min_lot_pp);

  // Rounding
  const round = num(quote.round_to_nearest);
  const rounded_pp = round > 0 ? mround(calc_pp, round) : calc_pp;

  // Extended = rounded × qty (so customer-visible math always adds up exactly)
  const extended = rounded_pp * q;

  return {
    quantity: q,
    setup_per_piece: setup_pp,
    programming_per_piece: prog_pp,
    total_setup_prog_per_piece: total_setup_pp,
    deburring_per_piece: deburr_pp,
    runtime_per_piece: runtime_pp,
    secondary_per_piece: secondary_pp,
    total_fab_per_piece: total_fab_pp,
    material_per_piece: material_pp,
    plating_per_piece: plating_pp,
    tooling_per_piece: tooling_pp,
    fixturing_per_piece: fixturing_pp,
    inspection_per_piece: inspection_pp,
    packaging_per_piece: packaging_pp,
    shipping_per_piece: shipping_pp,
    total_cog_per_piece: total_cog_pp,
    subtotal_per_piece: subtotal_pp,
    markup_per_piece: markup_pp,
    price_before_floor_rounding: price_pp,
    min_lot_price_per_piece: min_lot_pp,
    calculated_price_per_piece: calc_pp,
    rounded_price_per_piece: rounded_pp,
    extended_price: extended,
    // Flags for soft warnings
    min_lot_drove_price: min_lot_pp > price_pp,
  };
};

/**
 * Price every populated slot. Returns an array of length 6 with nulls in unused slots.
 * Use `priceAllBreaks(quote).filter(Boolean)` for the customer-quote view.
 */
export const priceAllBreaks = (quote) => {
  const breaks = quote.quantity_breaks || [];
  // Pad/trim to length 6 so the result is always 6 long
  const padded = [...breaks, 0, 0, 0, 0, 0, 0].slice(0, 6);
  return padded.map((q) => priceQuantityBreak(quote, Number(q)));
};

// ─────────────────────────────────────────────────────────────────────────────
// Quote number generation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Suggest the next quote number in the form Q-YYYY-NNNN.
 * Year-scoped, zero-padded to 4 digits. Matches the PO numbering convention.
 */
export const generateQuoteNumber = async () => {
  const year = new Date().getFullYear();
  const prefix = `Q-${year}-`;
  const { data, error } = await supabase
    .from('customer_quotes')
    .select('quote_number')
    .like('quote_number', `${prefix}%`)
    .order('quote_number', { ascending: false })
    .limit(1);
  if (error) throw error;

  let next = 1;
  if (data && data.length > 0) {
    const match = data[0].quote_number.match(/^Q-\d{4}-(\d+)$/);
    if (match) next = parseInt(match[1], 10) + 1;
  }
  return `${prefix}${String(next).padStart(4, '0')}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Validation helpers (per spec §4)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns an array of warning strings (empty if none). Soft warnings; the UI
 * surfaces these but doesn't block.
 */
export const getQuoteWarnings = (quote, results = null) => {
  const warnings = [];

  if (num(quote.markup_pct) === 0) {
    warnings.push('No markup applied — selling at cost.');
  }

  const breaks = (quote.quantity_breaks || []).filter((q) => q > 0);
  const ascending = breaks.every((q, i) => i === 0 || q >= breaks[i - 1]);
  if (!ascending) {
    warnings.push('Quantity breaks are not in ascending order — higher volumes typically get better pricing.');
  }

  if (quote.material && num(quote.material_cost_per_part) === 0) {
    warnings.push('Material specified but no cost entered.');
  }

  const computed = results || priceAllBreaks(quote);
  computed.forEach((r) => {
    if (r?.min_lot_drove_price) {
      warnings.push(`Minimum lot charge is driving the price on Qty ${r.quantity} — review.`);
    }
  });

  return warnings;
};

/**
 * Returns true if quote has all required fields (per spec §4.1).
 * UI uses this to gate Save / Generate.
 */
export const isQuoteValid = (quote) => {
  if (!quote) return false;
  if (!quote.customer_id) return false;
  if (!quote.drawing_number?.trim()) return false;
  if (!quote.shop_rate_per_hr || num(quote.shop_rate_per_hr) <= 0) return false;
  const breaks = quote.quantity_breaks || [];
  if (!breaks.some((q) => Number(q) > 0)) return false;
  return true;
};

// ─────────────────────────────────────────────────────────────────────────────
// Regression test (spec §7)
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// PDF helpers
// ─────────────────────────────────────────────────────────────────────────────

const PRIMARY = '#384f77';

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Image load failed: ${src}`));
    setTimeout(() => reject(new Error('Image load timeout')), 5000);
    img.src = src;
  });

// Synchronous header — caller preloads the logo image and passes it in.
const drawCompanyHeader = (doc, { title, docNumber, logoImg }) => {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(24);
  doc.setTextColor(PRIMARY);
  doc.text(title, pageWidth / 2, 14, { align: 'center' });

  if (docNumber) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(PRIMARY);
    doc.text(docNumber, 15, 20);
  }

  doc.setFontSize(12);
  doc.setTextColor(PRIMARY);
  doc.setFont('helvetica', 'bold');
  doc.text('Advanced Controls & Automation', 15, 30);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text('574 Hudson River Road', 15, 36);
  doc.text('Waterford, NY 12188', 15, 42);
  doc.text('518-369-4408', 15, 48);
  doc.text('sales@acamachine.com', 15, 54);

  if (logoImg) {
    try { doc.addImage(logoImg, 'PNG', pageWidth - 45, 30, 30, 30); }
    catch (err) { console.error('addImage logo failed:', err); }
  }
};

const drawAddressBlock = (doc, x, y, heading, addr) => {
  doc.setFontSize(10);
  doc.setTextColor(PRIMARY);
  doc.setFont('helvetica', 'bold');
  doc.text(heading, x, y);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  let yy = y + 6;
  const lines = [];
  if (addr.name) lines.push(addr.name);
  if (addr.contact) lines.push(addr.contact);
  if (addr.address) lines.push(addr.address);
  const csz = [addr.city, addr.state].filter(Boolean).join(', ');
  if (csz || addr.zip) lines.push(`${csz}${csz && addr.zip ? ' ' : ''}${addr.zip || ''}`);
  if (addr.phone) lines.push(`Phone: ${addr.phone}`);
  if (addr.email) lines.push(`Email: ${addr.email}`);
  if (lines.length === 0) lines.push('—');
  lines.forEach((line) => { doc.text(line, x, yy); yy += 6; });
  return yy;
};

const customerToAddresses = (customer) => {
  if (!customer) {
    return { billTo: { name: '—' }, shipTo: { name: '—' } };
  }
  const name = customer.nickname || customer.name || '—';
  return {
    billTo: {
      name,
      contact: customer.billing_contact_name,
      address: customer.billing_address,
      city: customer.billing_city,
      state: customer.billing_state,
      zip: customer.billing_zip,
      phone: customer.billing_contact_phone,
      email: customer.billing_contact_email,
    },
    shipTo: {
      name,
      contact: customer.shipping_contact_name,
      address: customer.shipping_address,
      city: customer.shipping_city,
      state: customer.shipping_state,
      zip: customer.shipping_zip,
      phone: customer.shipping_contact_phone,
      email: customer.shipping_contact_email,
    },
  };
};

// Synchronous — caller preloads the MIA image and passes it in.
const drawFooterImage = (doc, miaImg) => {
  if (!miaImg) return;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  try {
    doc.addImage(miaImg, 'PNG', pageWidth - 67.5, pageHeight - 25, 52.5, 15);
  } catch (err) {
    console.error('addImage MIA failed:', err);
  }
};

const sanitizeFilename = (s) => String(s || 'quote').replace(/[^a-z0-9._-]/gi, '_');

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

// ─────────────────────────────────────────────────────────────────────────────
// Customer Quote PDF (spec §3.2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generate and download the customer-facing quote document.
 * @param {object} quote   - customer_quotes row (markup_pct can be decimal or percent — we only display, never recompute)
 * @param {object} customer - joined customers row (or null)
 * @param {Array}  results - priceAllBreaks(normalizedQuote) — length-6 array with nulls in unused slots
 */
export const generateCustomerQuotePDF = async ({ quote, customer, results }) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Preload images so didDrawPage can use them synchronously
  let logoImg = null;
  let miaImg = null;
  try { logoImg = await loadImage(require('../assets/acaLogo.png')); } catch (e) { console.error(e); }
  try { miaImg = await loadImage(require('../assets/madeInAmerica.png')); } catch (e) { console.error(e); }

  drawCompanyHeader(doc, { title: 'QUOTE', docNumber: `Quote: ${quote.quote_number || 'N/A'}`, logoImg });

  // Address blocks
  const { billTo, shipTo } = customerToAddresses(customer);
  const billBottom = drawAddressBlock(doc, 15, 72, 'Bill To:', billTo);
  const shipBottom = drawAddressBlock(doc, pageWidth / 2 + 5, 72, 'Ship To:', shipTo);

  // Quote details (compact 4-col label/value table)
  const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString() : '—');
  const validUntil = () => {
    if (!quote.quote_date) return '—';
    const dt = new Date(quote.quote_date + 'T00:00:00');
    dt.setDate(dt.getDate() + (quote.quote_valid_days || 30));
    return dt.toLocaleDateString();
  };

  autoTable(doc, {
    startY: Math.max(billBottom, shipBottom) + 4,
    body: [
      ['Quote #',       quote.quote_number || '—', 'Quote Date',  fmtDate(quote.quote_date)],
      ['RFQ / PO',      quote.customer_rfq_po || '—', 'Valid Until', validUntil()],
      ['Drawing / Part #', quote.drawing_number || '—', 'Revision', quote.revision || '—'],
      ['Description',   quote.description || '—', 'Material',     quote.material || '—'],
      ['Payment Terms', quote.payment_terms || '—', 'Shipping',    quote.shipping_fob || '—'],
      ['Lead Time',     quote.lead_time || '—', 'Quoted By',       quote.quoted_by || '—'],
    ],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 1.5 },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: PRIMARY, cellWidth: 32 },
      1: { cellWidth: 'auto' },
      2: { fontStyle: 'bold', textColor: PRIMARY, cellWidth: 30 },
      3: { cellWidth: 'auto' },
    },
    margin: { left: 15, right: 15 },
  });

  // Pricing table — only populated quantity breaks
  const populated = (results || []).filter(Boolean);
  const pricingRows = populated.length > 0
    ? populated.map((r) => [
        String(r.quantity),
        quote.drawing_number || '—',
        quote.revision || '—',
        fmtMoney(r.rounded_price_per_piece),
        fmtMoney(r.extended_price),
        '',
      ])
    : [['—', '—', '—', '—', '—', '']];

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 4,
    head: [['QTY', 'DRAWING / PART', 'REV', 'PRICE / PC', 'EXTENDED', 'NOTES']],
    body: pricingRows,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 2, textColor: [0, 0, 0] },
    headStyles: { fillColor: PRIMARY, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { left: 15, right: 15, bottom: 35 },
    columnStyles: {
      0: { cellWidth: 14, halign: 'right' },
      1: { cellWidth: 40 },
      2: { cellWidth: 16 },
      3: { cellWidth: 28, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
      5: { cellWidth: 'auto' },
    },
    didDrawPage: () => { drawFooterImage(doc, miaImg); },
  });

  // Notes & signature footer
  let y = doc.lastAutoTable.finalY + 8;
  if (quote.customer_notes) {
    doc.setFontSize(10);
    doc.setTextColor(PRIMARY);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 15, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(quote.customer_notes, 15, y + 5, { maxWidth: pageWidth - 30 });
    y += 5 + Math.ceil(quote.customer_notes.length / 90) * 5;
  }

  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text(`Prepared by: ${quote.quoted_by || '—'}`, 15, y + 10);
  doc.text('Customer Approval: ____________________________', pageWidth - 95, y + 10);

  await savePdfWithDialog(doc, `${sanitizeFilename(quote.quote_number)}.pdf`);
};

// ─────────────────────────────────────────────────────────────────────────────
// Internal Estimate PDF (spec §3.1)
// ─────────────────────────────────────────────────────────────────────────────

const ESTIMATE_SECTIONS = [
  {
    title: 'Setup & Programming',
    rows: [
      ['Setup / piece',          'setup_per_piece'],
      ['Programming / piece',    'programming_per_piece'],
      ['Total Setup / piece',    'total_setup_prog_per_piece', true],
    ],
  },
  {
    title: 'Fabrication',
    rows: [
      ['Deburring / piece',      'deburring_per_piece'],
      ['Runtime / piece',        'runtime_per_piece'],
      ['Secondary / piece',      'secondary_per_piece'],
      ['Total Fab / piece',      'total_fab_per_piece', true],
    ],
  },
  {
    title: 'Cost of Goods',
    rows: [
      ['Material / piece',       'material_per_piece'],
      ['Plating / piece',        'plating_per_piece'],
      ['Tooling / piece',        'tooling_per_piece'],
      ['Fixturing / piece',      'fixturing_per_piece'],
      ['Inspection / piece',     'inspection_per_piece'],
      ['Packaging / piece',      'packaging_per_piece'],
      ['Shipping / piece',       'shipping_per_piece'],
      ['Total COG / piece',      'total_cog_per_piece', true],
    ],
  },
  {
    title: 'Quote Price',
    rows: [
      ['Subtotal / piece',          'subtotal_per_piece'],
      ['Markup / piece',            'markup_per_piece'],
      ['Price (pre-floor/round)',   'price_before_floor_rounding'],
      ['Minimum Lot / piece',       'min_lot_price_per_piece'],
      ['Calculated / piece',        'calculated_price_per_piece'],
      ['Rounded Price / piece',     'rounded_price_per_piece', false, true],
      ['Extended Price',            'extended_price', false, true],
    ],
  },
];

/**
 * Generate and download the internal cost-breakdown PDF.
 * Shows all 6 quantity columns including unused ones (per spec).
 */
export const generateInternalEstimatePDF = async ({ quote, customer, results }) => {
  const doc = new jsPDF({ orientation: 'landscape' });
  const pageWidth = doc.internal.pageSize.getWidth();

  // (internal doc doesn't need preloaded images — minimal header, no MIA footer)
  // Compact header
  doc.setFontSize(18);
  doc.setTextColor(PRIMARY);
  doc.setFont('helvetica', 'bold');
  doc.text('INTERNAL ESTIMATE', pageWidth / 2, 12, { align: 'center' });
  doc.setFontSize(8);
  doc.setTextColor(180, 0, 0);
  doc.text('NOT FOR CUSTOMER — INTERNAL USE ONLY', pageWidth / 2, 17, { align: 'center' });

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  doc.text(`Quote: ${quote.quote_number || '—'}`, 15, 24);
  doc.text(`Customer: ${customer?.nickname || customer?.name || '—'}`, 15, 30);
  doc.text(`Drawing / Part: ${quote.drawing_number || '—'}${quote.revision ? ` · rev ${quote.revision}` : ''}`, 15, 36);

  doc.text(`Quote Date: ${quote.quote_date ? new Date(quote.quote_date + 'T00:00:00').toLocaleDateString() : '—'}`, pageWidth - 80, 24);
  doc.text(`Shop Rate: $${Number(quote.shop_rate_per_hr || 0).toFixed(2)}/hr`, pageWidth - 80, 30);
  doc.text(`Quoted By: ${quote.quoted_by || '—'}`, pageWidth - 80, 36);

  // Build the matrix
  const padded = [...(results || []), null, null, null, null, null, null].slice(0, 6);
  const quantities = padded.map((r) => (r?.quantity ?? null));

  const head = [[
    'Line Item',
    ...quantities.map((q) => (q != null ? `Qty ${q}` : '—')),
  ]];

  const body = [];
  ESTIMATE_SECTIONS.forEach((section) => {
    body.push([
      { content: section.title, colSpan: 7, styles: { fillColor: [233, 238, 245], fontStyle: 'bold', textColor: PRIMARY } },
    ]);
    section.rows.forEach(([label, key, bold, highlight]) => {
      const row = [
        { content: label, styles: bold || highlight ? { fontStyle: 'bold' } : {} },
        ...padded.map((r) => {
          if (!r) return { content: '—', styles: { textColor: [200, 200, 200] } };
          const v = r[key];
          const cell = { content: fmtMoney(v) };
          if (highlight) cell.styles = { fillColor: [220, 244, 230], fontStyle: 'bold', textColor: [9, 75, 51] };
          else if (bold) cell.styles = { fontStyle: 'bold' };
          return cell;
        }),
      ];
      body.push(row);
    });
  });

  autoTable(doc, {
    startY: 42,
    head,
    body,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 1.5 },
    headStyles: { fillColor: PRIMARY, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'right' },
    columnStyles: {
      0: { cellWidth: 50, halign: 'left' },
      1: { cellWidth: 32, halign: 'right' },
      2: { cellWidth: 32, halign: 'right' },
      3: { cellWidth: 32, halign: 'right' },
      4: { cellWidth: 32, halign: 'right' },
      5: { cellWidth: 32, halign: 'right' },
      6: { cellWidth: 32, halign: 'right' },
    },
    margin: { left: 15, right: 15 },
  });

  await savePdfWithDialog(doc, `${sanitizeFilename(quote.quote_number)}_internal.pdf`);
};

/**
 * Verify the pricing engine against the spec's worked example.
 * Call from the browser console: `import('./utils/quoteUtils').then(m => console.log(m.verifyPricingEngine()))`
 * Returns { allOk, rows: [...] } — allOk should be true.
 */
export const verifyPricingEngine = () => {
  const quote = {
    shop_rate_per_hr: 150,
    setup_min: 75,         // 1.25 hr × 60
    programming_min: 60,   // 1.0 hr  × 60
    deburring_min: 1.0,
    runtime_min: 18.0,
    secondary_min: 2.5,
    inspection_min: 15,    // 0.25 hr × 60
    packaging_min: 6,      // 0.1 hr  × 60
    material_cost_per_part: 8.51,
    plating_total: 0,
    tooling_total: 0,
    fixturing_total: 0,
    shipping_total: 0,
    markup_pct: 0,
    minimum_lot_charge: 0,
    round_to_nearest: 0.01,
    quantity_breaks: [1, 4, 8, 10, 0, 0],
  };

  const expected = {
    1:  { rounded: 452.26, extended: 452.26  },
    4:  { rounded: 159.76, extended: 639.04  },
    8:  { rounded: 111.01, extended: 888.08  },
    10: { rounded: 101.26, extended: 1012.60 },
  };

  const epsilon = 0.005; // within half a cent

  const rows = [1, 4, 8, 10].map((q) => {
    const r = priceQuantityBreak(quote, q);
    const exp = expected[q];
    const dRounded = Math.abs(r.rounded_price_per_piece - exp.rounded);
    const dExt = Math.abs(r.extended_price - exp.extended);
    return {
      q,
      rounded_actual: Number(r.rounded_price_per_piece.toFixed(4)),
      rounded_expected: exp.rounded,
      extended_actual: Number(r.extended_price.toFixed(4)),
      extended_expected: exp.extended,
      ok: dRounded < epsilon && dExt < epsilon,
    };
  });

  return { rows, allOk: rows.every((r) => r.ok) };
};
